/**
 * Write → read invalidation signal.
 *
 * The China B-mono REST seam swapped `onSnapshot` (push) for
 * `usePolledResource` (a 20 s poll) and lost the one property push gave for
 * free: your own write echoes back to your own listeners immediately. Users
 * saw their own change take 20 s to appear, so they pressed F5. Every write in
 * the app funnels through `restClient.send()`, which announces here; the read
 * hooks listen and refetch. No call-site changes, and nothing new on the
 * network path (same-origin `/api`, so China reachability is unaffected).
 *
 * Lives in its own module rather than in `restClient` so the read hooks don't
 * import the write client — otherwise every test that mocks `restClient` would
 * have to stub this too.
 *
 * ponytail: one global signal — every mounted poller refetches on ANY write,
 * with no per-collection scoping. Worst case today is TimesheetView: ~6 polled
 * hooks, one of which (`useMultiProjectDesignations`) fans out to one request
 * per distinct project, so a cell edit costs roughly 15 small GETs (identical
 * URLs are shared in `restClient`, and kronos-api serves reference lists from
 * its cache). Scoping the signal
 * by collection would mean declaring, at all ~40 read call-sites, which
 * collections they depend on — do that only if a screen ever mounts enough
 * readers for the refetch wave to actually show.
 */
const listeners = new Set<() => void>();
let notifyTimer: ReturnType<typeof setTimeout> | undefined;

/** Subscribe to "a write landed". Returns an unsubscribe. */
export function onMutation(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announce a successful write. Coalesced so a burst of sequential writes
 * (milestone seeding, the budget / Clockify import loops) triggers one refetch
 * wave instead of one per doc — which matters most on the high-latency China
 * path.
 */
export function notifyMutation(): void {
  clearTimeout(notifyTimer);
  notifyTimer = setTimeout(() => {
    for (const listener of [...listeners]) listener();
  }, 120);
}
